import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

type EasterEggState = {
  /** Eggs found per year, keyed by year string (e.g. "2026") */
  eggsByYear: Record<string, number>;
  /** Set of egg IDs already found (to prevent re-showing) */
  foundEggIds: Set<string>;
  /** Collect an egg - increments count and marks as found */
  collectEgg: (eggId: string) => void;
  /** Get the count for the current year */
  getCurrentYearCount: () => number;
  /** Reset if the year has changed (called on init) */
  checkYearReset: () => void;
};

const getCurrentYear = () => new Date().getFullYear().toString();

export const useEasterEggStore = createWithEqualityFn<EasterEggState>()(
  persist(
    (set, get) => ({
      eggsByYear: {},
      foundEggIds: new Set<string>(),

      collectEgg: (eggId: string) => {
        const year = getCurrentYear();
        const state = get();
        if (state.foundEggIds.has(eggId)) return;

        const currentCount = state.eggsByYear[year] ?? 0;
        const newFoundIds = new Set(state.foundEggIds);
        newFoundIds.add(eggId);

        set({
          eggsByYear: { ...state.eggsByYear, [year]: currentCount + 1 },
          foundEggIds: newFoundIds,
        });
      },

      getCurrentYearCount: () => {
        const year = getCurrentYear();
        return get().eggsByYear[year] ?? 0;
      },

      checkYearReset: () => {
        const year = getCurrentYear();
        const state = get();
        // If we have eggs from previous years but none for current year,
        // clear the foundEggIds so eggs can appear fresh this year
        if (state.eggsByYear[year] === undefined && state.foundEggIds.size > 0) {
          set({ foundEggIds: new Set<string>() });
        }
      },
    }),
    {
      name: 'lio-easter-egg-hunt',
      version: 1,
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          // Rehydrate Set from array
          if (parsed?.state?.foundEggIds) {
            parsed.state.foundEggIds = new Set(parsed.state.foundEggIds);
          }
          return parsed;
        },
        setItem: (name, value: any) => {
          // Serialize Set to array
          const toStore = {
            ...value,
            state: {
              ...value.state,
              foundEggIds: Array.from(value.state.foundEggIds ?? []),
            },
          };
          localStorage.setItem(name, JSON.stringify(toStore));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
  shallow,
);

// Check for year reset on module load
useEasterEggStore.getState().checkYearReset();
